import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, FileSpreadsheet, Ban } from 'lucide-react';
import { api, download, post, put } from '../../lib/api.js';
import { errorMessage } from '../../lib/passkey.js';
import { Alert, fmtDate, Spinner } from '../../lib/ui.js';
import { Avatar, Badge, Card, Table, Th } from '../../lib/components.js';
import { usePhotos } from '../photos.js';
import { formatRupees } from '@siva/shared';
import { t } from '../../i18n/index.js';
import { Menu } from '../../lib/Menu.js';

type Row = {
  worker: { id: string; name: string; code: string | null };
  problem: { code: string; detail: string } | null;
  preview: {
    gross_rupees: number;
    deduction_rupees: number;
    overtime_rupees: number;
    days_not_served: number;
    tds_rupees: number;
    net_rupees: number;
    days_engaged: number;
    days_pending: number;
    has_bank_details: boolean;
    /** What is not on record, in words; empty when has_bank_details. */
    bank_missing: string[];
  } | null;
  document: {
    id: string;
    number: string;
    version: number;
    published_at: string;
    net_rupees: number;
  } | null;
};
type Month = { month: string; month_label: string; rows: Row[] };

/** The month before this one: what an owner is working on when they open this screen. */
function lastMonth(): string {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Payment advices for a month.
 *
 * One screen: the figures for everybody engaged that month, publish, download. A row that cannot
 * be computed says why instead of the numbers — a missing service fee is a thing to go and fix,
 * not a worker to quietly leave out of the run.
 */
/** YYYY-MM moved by n months. */
const shiftMonth = (month: string, n: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export function PaymentsPage() {
  const { photoOf } = usePhotos();
  const [month, setMonth] = useState(lastMonth());
  const [data, setData] = useState<Month | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /**
   * A month takes the best part of a second to compute, and switching months twice in that time
   * used to leave whichever response landed last on screen — August's figures under February's
   * heading. Each load claims a number and only the current one is allowed to set the table.
   */
  const request = useRef(0);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    const mine = ++request.current;
    // The table stays on screen while the next month arrives. Blanking it first made every
    // arrow and every save flash the page empty and back — the "sudden refresh" the owner saw.
    setLoading(true);
    try {
      const r = await api<Month>(`/admin/payment-advices?month=${month}`);
      if (mine !== request.current) return;
      setData(r);
      setError(null);
    } catch (err) {
      if (mine === request.current) setError(errorMessage(err));
    } finally {
      if (mine === request.current) setLoading(false);
    }
  }, [month]);
  useEffect(() => {
    void load();
  }, [load]);

  const publish = async (row: Row) => {
    setBusy(row.worker.id);
    setError(null);
    setNote(null);
    try {
      const r = await post<{ document: { number: string }; replaced: string | null }>(
        '/admin/payment-advices/publish',
        { month, user_id: row.worker.id },
      );
      setNote(
        r.replaced
          ? t('pay_published_replacing', { number: r.document.number, old: r.replaced })
          : t('pay_published', { number: r.document.number }),
      );
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  /** Everybody who can be published and has not been. Skips the rest, and says how many. */
  const publishAll = async () => {
    const todo = (data?.rows ?? []).filter((r) => r.preview?.has_bank_details && !r.document);
    if (todo.length === 0) return;
    setBusy('all');
    setError(null);
    setNote(null);
    let done = 0;
    try {
      for (const row of todo) {
        await post('/admin/payment-advices/publish', { month, user_id: row.worker.id });
        done++;
      }
      setNote(t('pay_published_all', { n: done }));
    } catch (err) {
      setError(`${t('pay_published_some', { n: done })} ${errorMessage(err)}`);
    } finally {
      setBusy(null);
      await load();
    }
  };

  /**
   * Withdraw a published document. The reason is mandatory and recorded — a document somebody was
   * handed and then had taken back is exactly the thing that needs an explanation attached to it
   * when it is read about a year later.
   */
  const withdraw = async (id: string, number: string) => {
    const reason = window.prompt(t('pay_withdraw_prompt', { number }));
    if (reason === null) return;
    if (reason.trim().length < 3) {
      setError(t('pay_withdraw_reason_short'));
      return;
    }
    setBusy(id);
    setError(null);
    try {
      await post(`/admin/documents/${id}/withdraw`, { reason });
      setNote(t('pay_withdrawn', { number }));
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const get = async (id: string, format: 'pdf' | 'xlsx', name: string) => {
    try {
      await download(`/admin/documents/${id}.${format}`, `${name}.${format}`);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const pending = data?.rows.some((r) => (r.preview?.days_pending ?? 0) > 0) ?? false;
  const toPublish = (data?.rows ?? []).filter(
    (r) => r.preview?.has_bank_details && !r.document,
  ).length;
  // Thirty rows all saying "no service fee is on record" is one fact, not thirty. Say it once.
  const noFeesAtAll =
    !!data && data.rows.length > 0 && data.rows.every((r) => r.problem?.code === 'no_fee');

  return (
    <div className="stack">
      <Card
        title={t('pay_title')}
        subtitle={data ? t('pay_sub', { month: data.month_label }) : t('pay_sub_loading')}
        actions={
          <span className="row card-actions-baseline">
            <label className="inline-field">
              {t('pay_month')}
              {/* The input comes first in the markup — a label names its first control — and the
                  arrows take their places either side of it by CSS order. */}
              <span className="month-nav">
                <input
                  type="month"
                  aria-label={t('pay_month')}
                  value={month}
                  max={lastMonth()}
                  onChange={(e) => e.target.value && setMonth(e.target.value)}
                />
                <button
                  type="button"
                  className="icon-btn small prev"
                  aria-label={t('pay_prev_month')}
                  onClick={() => setMonth(shiftMonth(month, -1))}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className="icon-btn small"
                  aria-label={t('pay_next_month')}
                  disabled={month >= lastMonth()}
                  onClick={() => setMonth(shiftMonth(month, 1))}
                >
                  ›
                </button>
              </span>
            </label>
            <button
              type="button"
              className="small"
              disabled={!!busy || toPublish === 0}
              onClick={() => void publishAll()}
            >
              {busy === 'all' ? t('pay_publishing') : t('pay_publish_all', { n: toPublish })}
            </button>
          </span>
        }
      >
        {error && <Alert>{error}</Alert>}
        {note && <Alert kind="success">{note}</Alert>}
        {pending && <Alert kind="warn">{t('pay_month_running')}</Alert>}
        {noFeesAtAll && <Alert kind="warn">{t('pay_no_fees_at_all')}</Alert>}

        {!data ? (
          <Spinner />
        ) : data.rows.length === 0 ? (
          <p className="muted">{t('pay_nobody')}</p>
        ) : (
          <Table className={`pay-table stacks${loading ? ' is-loading' : ''}`}>
            <thead>
              <tr>
                <Th>{t('pay_worker')}</Th>
                <Th>{t('pay_days')}</Th>
                <Th>{t('pay_gross')}</Th>
                <Th>{t('pay_deduction')}</Th>
                <Th>{t('pay_overtime')}</Th>
                <Th>{t('pay_tds')}</Th>
                <Th>{t('pay_net')}</Th>
                <Th>{t('pay_status')}</Th>
                <Th>{t('worker_actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.worker.id}>
                  {/* cell-name: on a phone the card leads with the name, full width, no label. */}
                  <td data-label={t('pay_worker')} className="cell-name">
                    {/* Name and code at the same weight read as two names. The name is the
                        thing; the code is the small print under it. */}
                    <span className="person">
                      <Avatar name={r.worker.name} size={28} photoUrl={photoOf(r.worker.id)} />
                      <span>
                        <Link to={`/workers/${r.worker.id}`}>
                          <strong>{r.worker.name}</strong>
                        </Link>
                        {r.worker.code && (
                          <>
                            <br />
                            <small className="muted code">{r.worker.code}</small>
                          </>
                        )}
                      </span>
                    </span>
                    {r.preview && !r.preview.has_bank_details && (
                      <>
                        <br />
                        <span className="badge amber">
                          {t('bank_missing_fields', { fields: r.preview.bank_missing.join(', ') })}
                        </span>
                      </>
                    )}
                  </td>
                  {r.problem ? (
                    <td colSpan={6} data-label={t('pay_gross')} className="muted">
                      {noFeesAtAll ? t('pay_no_fee_short') : r.problem.detail}
                    </td>
                  ) : (
                    <>
                      <td data-label={t('pay_days')} className="nowrap">
                        {r.preview!.days_engaged}
                        {r.preview!.days_not_served > 0 && (
                          <small className="muted">
                            {' '}
                            · {t('pay_not_served', { n: r.preview!.days_not_served })}
                          </small>
                        )}
                      </td>
                      <td data-label={t('pay_gross')} className="nowrap">
                        {formatRupees(r.preview!.gross_rupees)}
                      </td>
                      <td data-label={t('pay_deduction')} className="nowrap">
                        {r.preview!.deduction_rupees
                          ? `- ${formatRupees(r.preview!.deduction_rupees)}`
                          : '—'}
                      </td>
                      <td data-label={t('pay_overtime')} className="nowrap">
                        <OvertimeField
                          month={month}
                          userId={r.worker.id}
                          workerName={r.worker.name}
                          rupees={r.preview!.overtime_rupees}
                          onSaved={() => void load()}
                        />
                      </td>
                      <td data-label={t('pay_tds')} className="nowrap">
                        {r.preview!.tds_rupees ? `- ${formatRupees(r.preview!.tds_rupees)}` : '—'}
                      </td>
                      <td data-label={t('pay_net')} className="nowrap">
                        <strong>{formatRupees(r.preview!.net_rupees)}</strong>
                      </td>
                    </>
                  )}
                  <td data-label={t('pay_status')} className="slip-cell">
                    {r.document ? (
                      <>
                        <span className="nowrap">
                          <Badge tone="green" className="no-ml">
                            {t('pay_published_badge', { v: r.document.version })}
                          </Badge>{' '}
                          <small className="muted">{fmtDate(r.document.published_at, false)}</small>
                        </span>
                        <small className="muted slip-number">{r.document.number}</small>
                        {r.preview && r.preview.net_rupees !== r.document.net_rupees && (
                          <small className="warn-text">{t('pay_differs')}</small>
                        )}
                      </>
                    ) : (
                      <Badge tone="neutral" className="no-ml">
                        {t('pay_not_published')}
                      </Badge>
                    )}
                  </td>
                  <td className="actions" data-label={t('worker_actions')}>
                    <span className="row nowrap">
                      {r.preview && (
                        <button
                          type="button"
                          className="small secondary"
                          // A correction is only offered when there is something to correct:
                          // a slip that already says what the figures say cannot be corrected
                          // into itself, and the button sat there enabled on every row.
                          disabled={
                            !!busy ||
                            !r.preview.has_bank_details ||
                            (!!r.document && r.preview.net_rupees === r.document.net_rupees)
                          }
                          title={
                            !r.preview.has_bank_details
                              ? t('pay_needs_bank', { fields: r.preview.bank_missing.join(', ') })
                              : r.document && r.preview.net_rupees === r.document.net_rupees
                                ? t('pay_no_correction')
                                : undefined
                          }
                          onClick={() => void publish(r)}
                        >
                          {busy === r.worker.id
                            ? t('pay_publishing')
                            : r.document
                              ? t('pay_republish')
                              : t('pay_publish')}
                        </button>
                      )}
                      {r.document && (
                        <Menu
                          label={t('pay_actions_for', { name: r.worker.name })}
                          items={[
                            {
                              label: t('pay_pdf'),
                              icon: <Download size={15} aria-hidden="true" />,
                              onSelect: () =>
                                void get(
                                  r.document!.id,
                                  'pdf',
                                  r.document!.number.replace(/\//g, '-'),
                                ),
                            },
                            {
                              label: t('pay_xlsx'),
                              icon: <FileSpreadsheet size={15} aria-hidden="true" />,
                              onSelect: () =>
                                void get(
                                  r.document!.id,
                                  'xlsx',
                                  r.document!.number.replace(/\//g, '-'),
                                ),
                            },
                            // For a document that should never have been issued and cannot be
                            // corrected, because the figures it should have carried do not
                            // exist. Nothing is deleted: it stops reaching the worker.
                            {
                              label: t('pay_withdraw'),
                              icon: <Ban size={15} aria-hidden="true" />,
                              danger: true,
                              onSelect: () => void withdraw(r.document!.id, r.document!.number),
                            },
                          ]}
                        />
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card title={t('pay_tds_note_title')}>
        <p className="muted">{t('pay_tds_note')}</p>
        <p className="muted">{t('pay_tds_note_2')}</p>
      </Card>
    </div>
  );
}

/**
 * Overtime for one worker and month: an amount the admin types, saved when they leave the field
 * or press Enter. Nothing calculates it — the office decides what a month's overtime is worth —
 * and it goes on the pay slip as "Overtime" when it is more than nothing.
 */
function OvertimeField({
  month,
  userId,
  workerName,
  rupees,
  onSaved,
}: {
  month: string;
  userId: string;
  workerName: string;
  rupees: number;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(rupees ? String(rupees) : '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  useEffect(() => setValue(rupees ? String(rupees) : ''), [rupees]);
  const save = async () => {
    const n = value.trim() === '' ? 0 : Number(value);
    if (!Number.isInteger(n) || n < 0) {
      setState('error');
      return;
    }
    if (n === rupees) return;
    setState('saving');
    try {
      await put('/admin/payment-advices/overtime', { month, user_id: userId, rupees: n });
      setState('saved');
      onSaved();
    } catch {
      setState('error');
    }
  };
  return (
    <span className={`overtime-field ${state}`}>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        value={value}
        aria-label={t('pay_overtime_for', { name: workerName })}
        placeholder="0"
        onChange={(e) => {
          setValue(e.target.value);
          setState('idle');
        }}
        onBlur={() => void save()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
      {state === 'saving' && <small className="muted">{t('saving')}</small>}
      {state === 'saved' && <small className="ok">{t('saved')}</small>}
      {state === 'error' && <small className="bad">{t('pay_overtime_bad')}</small>}
    </span>
  );
}
